import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count, fetch_one } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';

import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';

import {
  Badge,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import {
  DataGrid,
  Dt,
  Fab,
  IconButton,
  Page
} from '../../../components/v2/styled';
import { SearchBox } from '../../../components/v2/SearchBox';
import { SnackDestroyed } from '../../../components/v2/snacks/Destroyed';

import { KnowledgeCourse } from '../../../models/KnowledgeCourse';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';

import HelpersPractice from '../../../actions/helpers/practice';
// const { DateTime } = require("luxon");

type Props = {}

export const CoachKnowledgeCourses: FunctionComponent<Props> = ({}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  let count = 0;
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<string>("created_at DESC");
  const [search, setSearch] = useState<string>("");

  const [destroyedOpen, setDestroyedOpen] = useState<boolean>(false);

  const is_knowledge = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'knowledge'});

  const {
    data: objects,
    isLoading: objectsLoading,
    isError: objectsError,
    isSuccess: objectsSuccess,
    refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["coach", "knowledge_courses", (login?.practice_id || currentPractice?.id), {page: page, order: order, search: search}],
    queryFn: () =>
      fetch_all_with_count<KnowledgeCourse>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_course',
          fields: ['id', 'name', 'enabled', 'knowledge_subscriptions|count', 'knowledge_subscriptions_with_approval|count', 'enabled', 'is_hidden', 'is_frontpage'],
          order: order,
          per_page: 20,
          page: page,
          filter: {
            search: search,
            advanced: {
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
    select: (d) => {
      count = d.count;
      return d.result;
    },
    enabled: !!(login?.practice_id || currentPractice?.id),
  });







  const mutationDestroy = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<KnowledgeCourse>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/remove`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setDestroyedOpen(true);
      objectsRefetch();
    },
  });








  const columns: GridColDef[] = [
    // { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: t("coach.models.knowledge_courses.name"), width: 250 },
    { field: 'knowledge_subscriptions_count', headerName: t("coach.models.knowledge_courses.sub_count"), width: 250 },
    { field: 'is_hidden', headerName: t('coach.models.knowledge_courses.is_hidden'), width: 120, sortable: false, renderCell: params => <>
      {!!params.row.is_hidden && <Check color='primary' />}
      {!params.row.is_hidden && <Close color='secondary' />}
    </> },
    { field: 'is_frontpage', headerName: t('coach.models.knowledge_courses.is_frontpage'), width: 120, sortable: false, renderCell: params => <>
      {!!params.row.is_frontpage && <Check color='primary' />}
      {!params.row.is_frontpage && <Close color='secondary' />}
    </> },
  ];

  return <Page title={t("coach.menu.knowledge_courses")} premiumGatekeeping={!!is_knowledge} premiumMode='knowledge'>
    <SearchBox
      search={search}
      setSearch={setSearch}
      objectCount={count}
    />

    <DataGrid
      action_count={4}
      hideCount
      actions={(params) => {
        return (<>
          <IconButton title="Edit" color="default" path={`/knowledge_course/${params.row.id}`}>
            <EditIcon />
          </IconButton>
          <IconButton title="Remove" destroyBtn color="default" onClick={() => {
            mutationDestroy.mutate({
              formData: {
                object: 'knowledge_course',
                id: params.row.id,
                ob: {
                  id: params.row.id,
                }
              }
            });
          }}>
            <DeleteIcon />
          </IconButton>
          <Badge badgeContent={params.row.knowledge_subscriptions_with_approval_count} color="warning" sx={{
            "& > span": {
              
            }
          }}>
            <IconButton title="Subscribers" color="default" path={`/knowledge_course/${params.row.id}/subs`}>
              <GroupsIcon />
            </IconButton>
          </Badge>
          <IconButton title="Builder" color="default" path={`/knowledge_course/${params.row.id}/builder`}>
            <BuildIcon />
          </IconButton>
        </>);
      }}
      objectCount={count}
      getRowClassName={(params) => `datagrid-row-enabled-${params.row.enabled}`}
      objects={(objects || []).map(u => ({
        ...u
      }))}
      columns={columns}
      loader={objectsLoading}
      onSortModelChange={(a, b) => {
        let a_0_field:string = a[0].field;
        if (a_0_field === 'name') a_0_field = `last_name ${a[0].sort}, first_name`;
        setOrder([a_0_field, a[0].sort].join(' '));
      }}
      page={page}
      setPage={(p) => {setPage(p);}}
    />

    <SnackDestroyed open={destroyedOpen} setOpen={setDestroyedOpen} />

    <Fab
      onClick={() => {
        navigate("/knowledge_course/new", {});
      }}
    />
  </Page>;
}
